import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Crop as CropIcon, Image as ImageIcon, Trash } from '@phosphor-icons/react';
import { publicURL, uploadMedia } from '@/api/media';
import { LogoImg } from '@/components/ui/logo-img';
import { ImageCropDialog } from '@/components/ImageCropDialog';

// Shared logo picker: drag-and-drop / click-to-upload with inline preview and
// a crop step. Used for the organisation logo (settings → general) and the
// invoice-header logo override (settings → billing). The top-level label and
// hint are caller-supplied; the smaller upload micro-copy is reused from the
// generic `settings.organization.logo.*` keys since it isn't org-specific.
export function LogoUploadField({
  slug,
  token,
  value,
  onChange,
  label,
  hint,
}: {
  slug: string;
  token: string | null;
  value: string;
  onChange: (objectKey: string) => void;
  label: string;
  hint: string;
}) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  async function onPickFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const asset = await uploadMedia(token, slug, file);
      onChange(asset.object_key);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setUploading(false);
    }
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  }
  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    setDragActive(false);
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('settings.organization.logo.notImage'));
      return;
    }
    void onPickFile(file);
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
      <div
        className="relative w-full"
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          aria-label={
            value
              ? t('settings.organization.logo.replace')
              : t('settings.organization.logo.upload')
          }
          className={`group relative h-48 w-full overflow-hidden rounded-2xl border border-dashed text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
            dragActive
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-muted/40 hover:bg-muted hover:text-foreground'
          }`}
        >
          {value ? (
            <LogoImg
              objectKey={value}
              size="medium"
              className="h-full w-full object-contain p-3"
            />
          ) : (
            <span className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
              <ImageIcon className="h-8 w-8" weight="duotone" aria-hidden />
              <span className="text-xs font-medium">
                {uploading
                  ? t('settings.organization.logo.uploading')
                  : dragActive
                    ? t('settings.organization.logo.dropHere')
                    : t('settings.organization.logo.cta')}
              </span>
            </span>
          )}
        </button>
        {value && !uploading && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCropOpen(true);
              }}
              aria-label={t('imageInput.crop')}
              className="absolute right-12 top-2 flex h-8 w-8 items-center justify-center rounded-pill bg-background/90 text-foreground shadow-md transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={t('imageInput.crop')}
            >
              <CropIcon weight="bold" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              aria-label={t('settings.organization.logo.remove')}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-pill bg-background/90 text-foreground shadow-md transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash weight="bold" className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onPickFile(file);
          e.target.value = '';
        }}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {value && (
        <ImageCropDialog
          open={cropOpen}
          src={publicURL(value)}
          filename={value}
          onCrop={async (file) => {
            const asset = await uploadMedia(token, slug, file);
            onChange(asset.object_key);
          }}
          onClose={() => setCropOpen(false)}
        />
      )}
    </div>
  );
}
